import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import { calculateClientAge } from '../clientAgeCalculation';
import {
  deriveIneligibleDayBreakdown,
  deriveIneligibilityReason,
} from '../deriveIneligibilityReason';
import { deriveStandardMilestones } from '../deriveMilestones';
import { DAYS_IN_MONTH } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  growthFactor,
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxDailyBenefitAmount,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';

// OneAmerica Indexed Annuity Care: single-premium indexed annuity with a base
// LTC Benefit Balance plus an optional COB rider. We model the base layer from
// the extracted Guaranteed Rates row at expected LTC start: LTC Benefit Balance
// plus LTC Monthly Withdrawal Limit. We do not rebuild the account/cash-
// surrender schedule in the projection strategy.
//
// INFLATION ANCHORS — the two pools deliberately answer "inflated since
// when?" differently, and both match the carrier (P2-34, verified against the
// illustration figures transcribed in docs/oa-indexed-annuity-care/
// field-mapping.md):
//  - BASE is frozen at the extracted Guaranteed Rates row. The carrier's
//    guaranteed crediting growth through the expected LTC-start age is already
//    EMBEDDED in the extracted LTCBB/limit (the extraction rule targets the
//    row at that age), so re-inflating it here would double-count.
//  - COB is POLICY-YEAR-INDEXED from issue: the carrier's COB rider page
//    projects the monthly limit as initial × (1+r)^(policyYear−1) — the
//    in-repo sample's $3,347 initial reproduces the printed year-10 $4,367
//    (×1.03^9) and year-20 $5,869 (×1.03^19) exactly. `scheduledCobValues`
//    therefore grows from policy year 1, NOT from claim start (simple or
//    compound per `isSimpleInflationProtection`).

export type IndexedAnnuityCareState = Record<string, never>;

export interface IndexedAnnuityCareDeltas {
  baseBenefitDays: number;
  cobBenefitDays: number;
  baseMonthlyLimit: number;
  cobMonthlyLimit: number;
  accountValueReduction: number;
}

export interface IndexedAnnuityCareScheduleRow {
  policyYear: number;
  accountValue: number;
  cashSurrenderValue: number;
  baseLtcBenefitBalance: number;
  baseMonthlyWithdrawalLimit: number;
}

interface IndexedAnnuityCareConfig {
  baseBenefitPoolAtIssue: number;
  baseMonthlyLimitAtIssue: number;
  extensionOfBenefitsPoolAtIssue: number;
  accumulatedValueAtIssue: number;
  baseBenefitDurationMonths: number;
  extensionBenefitDurationMonths: number;
  extensionBenefitInflationRate: number;
  extensionBenefitInflationCapYears: number;
  extensionBenefitInflationIsSimple: boolean;
}

const BASE_BENEFIT_PERIOD_MONTHS = 24;
const COB_BENEFIT_PERIOD_MONTHS = 24;

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function readPositiveMonths(value: number | null | undefined, fallback: number): number {
  return isFiniteNumber(value) && value > 0 ? value : fallback;
}

function readConfig(ltcPolicy: PolicyFundingSource): IndexedAnnuityCareConfig {
  const baseBenefitPoolAtIssue =
    ltcPolicy.policyBaseBenefitAmountAtIssue ?? ltcPolicy.policyMaximumBenefitAmount ?? 0;
  const extractedMonthlyLimit = getMaxDailyBenefitAmount(ltcPolicy) * DAYS_IN_MONTH;
  const baseBenefitDurationMonths = readPositiveMonths(
    ltcPolicy.policyAccelerationBenefitDurationMonths,
    BASE_BENEFIT_PERIOD_MONTHS,
  );
  const extensionOfBenefitsPoolAtIssue =
    ltcPolicy.policyContinuationBenefitAmountAtIssue ??
    ltcPolicy.policyContinuationBenefitAmount ??
    0;
  const accumulatedValueAtIssue =
    ltcPolicy.policyAccumulatedValueAtIssue ?? ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;

  return {
    baseBenefitPoolAtIssue,
    baseMonthlyLimitAtIssue:
      extractedMonthlyLimit > 0
        ? extractedMonthlyLimit
        : baseBenefitPoolAtIssue / baseBenefitDurationMonths,
    extensionOfBenefitsPoolAtIssue,
    accumulatedValueAtIssue,
    baseBenefitDurationMonths,
    extensionBenefitDurationMonths: readPositiveMonths(
      ltcPolicy.policyEobBenefitDurationMonths,
      COB_BENEFIT_PERIOD_MONTHS,
    ),
    // Never default to an assumed inflation amount. When neither the
    // COB-specific nor the policy-wide rate is extracted, the COB pool is held
    // flat (rate 0) rather than silently compounded at an assumed rate.
    extensionBenefitInflationRate:
      ltcPolicy.policyInflationProtectionOnCOBAmount ??
      ltcPolicy.policyInflationProtection ??
      0,
    extensionBenefitInflationCapYears: ltcPolicy.policyInflationProtectionYears ?? 0,
    extensionBenefitInflationIsSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

export function indexedAnnuityCareGuaranteedExtractedRow(
  ltcPolicy: PolicyFundingSource,
): IndexedAnnuityCareScheduleRow {
  const config = readConfig(ltcPolicy);
  return {
    policyYear: 1,
    accountValue: config.accumulatedValueAtIssue,
    cashSurrenderValue: 0,
    baseLtcBenefitBalance: config.baseBenefitPoolAtIssue,
    baseMonthlyWithdrawalLimit: config.baseMonthlyLimitAtIssue,
  };
}

function scheduledCobValues(
  config: IndexedAnnuityCareConfig,
  policyYear: number,
): {
  monthlyLimit: number;
  balance: number;
} {
  const initialMonthlyLimit =
    config.extensionBenefitDurationMonths > 0
      ? config.extensionOfBenefitsPoolAtIssue / config.extensionBenefitDurationMonths
      : 0;
  const factor = growthFactor(
    config.extensionBenefitInflationRate,
    config.extensionBenefitInflationCapYears,
    config.extensionBenefitInflationIsSimple,
    policyYear,
  );
  const monthlyLimit = initialMonthlyLimit * factor;
  return {
    monthlyLimit,
    balance: monthlyLimit * config.extensionBenefitDurationMonths,
  };
}

function applyPriorUtilization({
  baseBalance,
  cobBalance,
  accountValue,
  utilizedToDate,
}: {
  baseBalance: number;
  cobBalance: number;
  accountValue: number;
  utilizedToDate: number;
}) {
  const priorBaseUtilization = Math.min(Math.max(utilizedToDate, 0), baseBalance);
  const priorCobUtilization = Math.min(
    Math.max(utilizedToDate - priorBaseUtilization, 0),
    cobBalance,
  );
  const accountValueReduction = Math.min(priorBaseUtilization, accountValue);

  return {
    baseBenefitPool: Math.max(baseBalance - priorBaseUtilization, 0),
    extensionOfBenefitsPool: Math.max(cobBalance - priorCobUtilization, 0),
    deathBenefit: Math.max(accountValue - accountValueReduction, 0),
  };
}

function emptyIndexedAnnuityCareDeltas(
  baseMonthlyLimit = 0,
  cobMonthlyLimit = 0,
): IndexedAnnuityCareDeltas {
  return {
    baseBenefitDays: 0,
    cobBenefitDays: 0,
    baseMonthlyLimit,
    cobMonthlyLimit,
    accountValueReduction: 0,
  };
}

export const indexedAnnuityCareStrategy: PolicyProjectionStrategy<
  IndexedAnnuityCareState,
  IndexedAnnuityCareDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<IndexedAnnuityCareState> {
    const config = readConfig(ltcPolicy);
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const scheduledBase = indexedAnnuityCareGuaranteedExtractedRow(ltcPolicy);
    const scheduledCob = scheduledCobValues(config, claimStartPolicyYear);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const openingPools = applyPriorUtilization({
      baseBalance: scheduledBase.baseLtcBenefitBalance,
      cobBalance: scheduledCob.balance,
      accountValue: scheduledBase.accountValue,
      utilizedToDate,
    });

    return {
      pools: openingPools,
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const config = readConfig(ltcPolicy);
    const initialBase = indexedAnnuityCareGuaranteedExtractedRow(ltcPolicy);
    const initialCob = scheduledCobValues(config, 1);
    const baseDaily = initialBase.baseMonthlyWithdrawalLimit / DAYS_IN_MONTH;
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);

    // Prior utilization deducted across the base + COB LTC pools at claim start
    // (mirrors `initClaimState`'s `applyPriorUtilization`). The account-value
    // reduction is a separate floor concern and does not feed "Drawn to date".
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);
    const claimStartCobBalance = scheduledCobValues(config, claimStartPolicyYear).balance;
    const priorBaseUtilization = Math.min(
      Math.max(utilizedToDate, 0),
      initialBase.baseLtcBenefitBalance,
    );
    const priorCobUtilization = Math.min(
      Math.max(utilizedToDate - priorBaseUtilization, 0),
      claimStartCobBalance,
    );
    const priorUtilization = priorBaseUtilization + priorCobUtilization;

    return {
      strategyId: 'indexed-annuity-care',
      priorUtilization,
      nominalDailyBenefit: baseDaily,
      nominalDailyBenefitBase: baseDaily,
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: baseDaily,
        [CareEnvironment.assistedLiving]: baseDaily,
        [CareEnvironment.fullCareFacility]: baseDaily,
        [CareEnvironment.independentLiving]: baseDaily,
      },
      inflationRate: 0,
      inflationCapYears: 0,
      inflationIsSimple: config.extensionBenefitInflationIsSimple,
      baseBenefitPoolAtIssue: initialBase.baseLtcBenefitBalance,
      extensionOfBenefitsPoolAtIssue: initialCob.balance,
      baseBenefitPoolDurationMonths: config.baseBenefitDurationMonths,
      extensionOfBenefitsPoolDurationMonths: config.extensionBenefitDurationMonths,
      extensionOfBenefitsPoolInflationRate: config.extensionBenefitInflationRate,
      extensionOfBenefitsPoolInflationCapYears: config.extensionBenefitInflationCapYears,
      accumulatedValueAtIssue: initialBase.accountValue,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: true,
      isIndemnity: false,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const config = readConfig(ltcPolicy);
    const scheduledBase = indexedAnnuityCareGuaranteedExtractedRow(ltcPolicy);
    const scheduledCob = scheduledCobValues(config, policyYear);
    const totalLtc = scheduledBase.baseLtcBenefitBalance + scheduledCob.balance;

    return {
      policyYear,
      effectiveDailyBenefit: scheduledBase.baseMonthlyWithdrawalLimit / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: scheduledBase.baseMonthlyWithdrawalLimit / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: scheduledCob.monthlyLimit / DAYS_IN_MONTH,
      effectiveFaceAmount: scheduledBase.baseLtcBenefitBalance,
      effectiveContinuationBenefit: scheduledCob.balance,
      effectiveTotalLtcBenefit: totalLtc,
      monthlyBenefitCap: scheduledBase.baseMonthlyWithdrawalLimit,
      continuationMonthlyBenefitCap: scheduledCob.monthlyLimit,
      theoreticalLtcPoolCeiling: totalLtc,
    };
  },

  computeMonth(
    input: MonthComputeInput<IndexedAnnuityCareState, IndexedAnnuityCareDeltas>,
  ): MonthlyEntry<IndexedAnnuityCareState, IndexedAnnuityCareDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );
    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<IndexedAnnuityCareState, IndexedAnnuityCareDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: emptyIndexedAnnuityCareDeltas(),
      });
    }

    const config = readConfig(ltcPolicy);
    const scheduledBase = indexedAnnuityCareGuaranteedExtractedRow(ltcPolicy);
    const scheduledCob = scheduledCobValues(config, policyYear);

    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    let baseScheduleAccrual = 0;
    let cobScheduleAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorPolicyYear = priorMonth.context.policyYear;
      const priorCob = scheduledCobValues(config, priorPolicyYear);
      if (openState.pools.extensionOfBenefitsPool > 0 && priorCob.balance > 0) {
        // The carrier's COB schedule assumes no draws; scale the anniversary
        // step proportionally for partially-drawn pools.
        cobScheduleAccrual =
          (scheduledCob.balance - priorCob.balance) *
          (openState.pools.extensionOfBenefitsPool / priorCob.balance);
      }
    }

    const baseAfterAccrual = openState.pools.baseBenefitPool + baseScheduleAccrual;
    const cobAfterAccrual = openState.pools.extensionOfBenefitsPool + cobScheduleAccrual;
    const baseDailyCap = scheduledBase.baseMonthlyWithdrawalLimit / DAYS_IN_MONTH;
    const cobDailyCap = scheduledCob.monthlyLimit / DAYS_IN_MONTH;
    const basePerDay = Math.min(costMetrics.dailyCareCost, baseDailyCap);
    const baseBenefitDays =
      basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
    const drawFromBase = baseBenefitDays * basePerDay;

    const cobPerDay = Math.min(costMetrics.dailyCareCost, cobDailyCap);
    const cobBenefitDays =
      cobPerDay > 0
        ? Math.min(Math.max(eligibleDays - baseBenefitDays, 0), cobAfterAccrual / cobPerDay)
        : 0;
    const drawFromCob = cobBenefitDays * cobPerDay;
    const ltcCoveragePaid = drawFromBase + drawFromCob;

    const closeBase = baseAfterAccrual - drawFromBase;
    const closeCob = cobAfterAccrual - drawFromCob;
    const accountValueReduction = Math.min(openState.pools.deathBenefit, drawFromBase);
    const closeDeathBenefit = openState.pools.deathBenefit - accountValueReduction;
    // A month drawing ANY base dollars reports the base cap; only months
    // funded purely from the COB pool report the COB cap.
    const inCobPhase = drawFromBase <= 0 && (drawFromCob > 0 || (closeBase <= 0 && closeCob > 0));

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase
        ? scheduledCob.monthlyLimit
        : scheduledBase.baseMonthlyWithdrawalLimit,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: 0,
    };

    const deltas: MonthlyDeltas<IndexedAnnuityCareDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseScheduleAccrual },
      eob: { depletion: drawFromCob, inflationAccrual: cobScheduleAccrual },
      deathBenefit: {
        reduction: accountValueReduction,
        flooredAtMinimum:
          openState.pools.deathBenefit > 0 &&
          closeDeathBenefit === 0 &&
          drawFromBase > accountValueReduction,
      },
      strategyDeltas: {
        baseBenefitDays,
        cobBenefitDays,
        baseMonthlyLimit: scheduledBase.baseMonthlyWithdrawalLimit,
        cobMonthlyLimit: scheduledCob.monthlyLimit,
        accountValueReduction,
      },
    };

    const closeState: MonthlyState<IndexedAnnuityCareState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeCob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeCob <= 0,
      clientPoolExhausted: closeBase + closeCob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<IndexedAnnuityCareState, IndexedAnnuityCareDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      emitMinimumDeathBenefitMilestone: false,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      // Only the COB rider inflates on IAC (the base balance follows the
      // extracted guaranteed schedule, not an inflation rider). A no-rider
      // policy (0% COB rate) gets no anniversary milestones (P2-33).
      hasInflationProtection: config.extensionBenefitInflationRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<IndexedAnnuityCareState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<IndexedAnnuityCareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<IndexedAnnuityCareState>): number {
    return closingState.pools.deathBenefit;
  },
};
